import React, { useState, useRef } from 'react';
import { Camera, Upload, X, Image as ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { processImageFiles } from '../../../utils/imageUtils';

interface ImageUploadProps {
  images: string[];
  thumbnailImages?: string[];
  onImagesChange: (images: string[], thumbnailImages: string[]) => void;
  maxImages?: number;
  onOpenPreview?: (index: number) => void;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  images,
  thumbnailImages = [],
  onImagesChange,
  maxImages = 10,
  onOpenPreview
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    console.log('📁 画像アップロード開始', { fileCount: files.length });
    setIsUploading(true);

    try {
      console.log('🔄 画像処理開始...');
      const result = await processImageFiles(files, images, thumbnailImages);
      
      if (result) {
        const { originals, thumbnails } = result;
        console.log('✨ 画像処理完了', { originals: originals.length, thumbnails: thumbnails.length });
        
        onImagesChange(originals, thumbnails);
        console.log('🎉 データ更新完了');
      }
    } catch (error) {
      console.error('❌ 画像アップロードエラー:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = (index: number) => {
    console.log('🗑️ 画像削除', { index });
    const newImages = [...images];
    const newThumbnails = [...thumbnailImages];
    newImages.splice(index, 1);
    newThumbnails.splice(index, 1);
    onImagesChange(newImages, newThumbnails);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    
    // Create a custom drag preview
    const dragPreview = document.createElement('div');
    dragPreview.className = 'bg-white p-2 rounded shadow-lg border border-blue-500';
    dragPreview.style.width = '100px';
    dragPreview.style.height = '100px';
    dragPreview.style.backgroundImage = `url(${thumbnailImages[index] || images[index]})`;
    dragPreview.style.backgroundSize = 'cover';
    dragPreview.style.backgroundPosition = 'center';
    dragPreview.style.opacity = '0.8';
    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 50, 50);
    
    // Remove the preview element after it's been used
    setTimeout(() => {
      document.body.removeChild(dragPreview);
    }, 0);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;
    
    const newImages = [...images];
    const newThumbnails = [...thumbnailImages];
    
    // Move the dragged item to the new position
    const [movedImage] = newImages.splice(draggedIndex, 1);
    const [movedThumbnail] = newThumbnails.splice(draggedIndex, 1);
    
    newImages.splice(dropIndex, 0, movedImage);
    newThumbnails.splice(dropIndex, 0, movedThumbnail);
    
    onImagesChange(newImages, newThumbnails);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleMoveImage = (index: number, direction: 'left' | 'right') => {
    const newIndex = direction === 'left' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= images.length) return;
    
    const newImages = [...images];
    const newThumbnails = [...thumbnailImages];
    
    // Swap the images
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    [newThumbnails[index], newThumbnails[newIndex]] = [newThumbnails[newIndex], newThumbnails[index]];
    
    onImagesChange(newImages, newThumbnails);
  };

  const remainingSlots = maxImages - images.length;

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
        <div className="text-center">
          <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-4">
            <label htmlFor="file-upload" className="cursor-pointer">
              <span className="mt-2 block text-sm font-medium text-gray-900">
                画像をアップロード
              </span>
              <span className="text-sm text-gray-500">
                残り {remainingSlots} 枚まで追加可能
              </span>
            </label>
            <input
              ref={fileInputRef}
              id="file-upload"
              name="file-upload"
              type="file"
              className="sr-only"
              multiple
              accept="image/*"
              onChange={(e) => handleFileUpload(e.target.files)}
              disabled={isUploading || remainingSlots <= 0}
            />
          </div>
          <div className="mt-4 flex justify-center space-x-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || remainingSlots <= 0}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              <Upload className="mr-2 h-4 w-4" />
              {isUploading ? 'アップロード中...' : 'ファイルを選択'}
            </button>
          </div>
        </div>
      </div>

      {/* Image Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {images.map((image, index) => (
            <div 
              key={`${image}-${index}`} 
              className={`relative group aspect-square cursor-move ${
                draggedIndex === index ? 'opacity-50 scale-95' : ''
              }`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              <img
                src={thumbnailImages[index] || image}
                alt={`画像 ${index + 1}`}
                className="w-full h-full object-cover rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-colors"
                onClick={() => onOpenPreview?.(index)}
              />
              
              {/* 画像番号表示 */}
              <div className="absolute top-2 left-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded-full">
                {index + 1}
              </div>
              
              {/* ホバー時のオーバーレイ */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-200 rounded-lg" />
              
              {/* 並べ替えボタン */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                {/* 左に移動ボタン */}
                {index > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveImage(index, 'left');
                    }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors shadow-lg"
                    title="左に移動"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                )}
                
                {/* 右に移動ボタン */}
                {index < images.length - 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveImage(index, 'right');
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors shadow-lg"
                    title="右に移動"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {/* 削除ボタン */}
              <button
                type="button"
                onClick={() => handleRemoveImage(index)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 shadow-lg z-10"
                title="画像を削除"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};